import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Modal, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import SignatureCanvas from 'react-signature-canvas';
import AddressAutocomplete from '../addressAutocomplete';
import { handleImageUpload } from '../../api/uploads';
import {
  submitChildRegistration,
  fetchRegistrationPackages,
  selectRegistrationById,
  selectSubmitLoading
} from '../../store/childRegistration/childRegistrationSlice';

const ChildRegistrationFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  // Get registration data from navigation state if available
  const navigationRegistrationData = location?.state?.registrationData;

  // Signature refs
  const arrivalSignaturePadRef = useRef(null);
  const emergencyMedicalSignaturePadRef = useRef(null);
  const mediaReleaseSignaturePadRef = useRef(null);
  const checklistSignaturePadRef = useRef(null);
  const contractParentSignaturePadRef = useRef(null);
  const contractFinancialSignaturePadRef = useRef(null);
  const offsiteSignaturePadRef = useRef(null);
  const contractClauseSignatureRef = useRef(null);

  // Contract signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentClauseIndex, setCurrentClauseIndex] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);

  // Authorized pickup persons state - track active person slots
  const [activePickupPersons, setActivePickupPersons] = useState([1]);

  const existingRegistration = useSelector((state) => selectRegistrationById(state, id));
  const submitLoading = useSelector(selectSubmitLoading);

  // Get user profile data to auto-populate child name
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;

  const [formData, setFormData] = useState({
    // ID for edit mode
    id: null,
    
    // Child Information
    child_first_name: profileData?.first_name || '',
    child_last_name: profileData?.last_name || '',
    child_dob: '',
    child_age: '',
    child_address: '',
    child_city: '',
    child_postal_code: '',

    // Parent/Guardian 1 Information
    parent1_first_name: '',
    parent1_last_name: '',
    parent1_phone: '',
    parent1_cell: '',
    parent1_email: '',
    parent1_address: '',
    parent1_city: '',
    parent1_state: '',
    parent1_zip: '',
    parent1_work_phone: '',
    parent1_work_address: '',
    parent1_work_city: '',
    parent1_work_postal_code: '',
    parent1_relation: 'Mother',

    // Parent/Guardian 2 Information
    parent2_first_name: '',
    parent2_last_name: '',
    parent2_phone: '',
    parent2_cell: '',
    parent2_email: '',
    parent2_address: '',
    parent2_city: '',
    parent2_state: '',
    parent2_zip: '',
    parent2_work_phone: '',
    parent2_work_address: '',
    parent2_work_city: '',
    parent2_work_postal_code: '',
    parent2_relation: 'Father',
    same_address: false,
    same_as_mother_address: false,

    // Emergency Contact Information
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_address: '',
    emergency_contact_city: '',
    emergency_contact_postal_code: '',
    emergency_contact_phone: '',
    emergency_contact_cell: '',
    emergency_contact_work_phone: '',
    emergency_contact_work_address: '',
    emergency_contact_work_city: '',
    emergency_contact_work_postal_code: '',
    emergency_contact_email: '',

    // Medical Information
    pediatrician_name: '',
    pediatrician_phone: '',
    medical_conditions: '',
    allergies: '',
    medications: '',

    // Additional Information
    special_needs: '',
    previous_daycare: '',
    enrollment_date: '',
    notes: '',

    // Authorized Pick-Up List (4 entries)
    pickup1_name: '',
    pickup1_phone: '',
    pickup1_cell: '',
    pickup1_address: '',
    pickup1_city: '',
    pickup1_postal_code: '',
    pickup2_name: '',
    pickup2_phone: '',
    pickup2_cell: '',
    pickup2_address: '',
    pickup2_city: '',
    pickup2_postal_code: '',
    pickup3_name: '',
    pickup3_phone: '',
    pickup3_cell: '',
    pickup3_address: '',
    pickup3_city: '',
    pickup3_postal_code: '',
    pickup4_name: '',
    pickup4_phone: '',
    pickup4_cell: '',
    pickup4_address: '',
    pickup4_city: '',
    pickup4_postal_code: '',

    // Arrival and Departure
    dropoff_time: '',
    pickup_time: '',
    parent_guardian_signature: '',
    executive_director_signature: '',
    start_date: '',
    withdrawal_date: '',

    // Emergency Medical Authorization
    emergency_auth_parent_name: '',
    emergency_auth_child_name: profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : '',
    family_physician_name: '',
    physician_address: '',
    physician_city: '',
    physician_postal_code: '',
    physician_phone: '',
    current_medications: '',
    health_allergies: '',
    sleep_time: '',
    wake_time: '',
    favourite_activity: '',
    emergency_auth_signature: '',
    emergency_auth_date: '',

    // Child Information Page
    agency_involvement: '',
    vision_history: '',
    hearing_concerns: '',
    diagnoses: '',
    communicable_disease: '',
    eating_concerns: '',
    developmental_concerns: '',
    pets_at_home: '',
    siblings_info: '',
    favourite_toy: '',

    // Media Release Waiver
    media_release_agree: false,
    media_release_child_name: profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : '',
    media_release_date: '',
    media_release_parent_name: '',
    media_release_signature: '',

    // Off-Site Permission
    offsite_agree: false,
    offsite_parent_name: '',
    offsite_child_name: profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : '',
    offsite_signature: '',
    offsite_date: '',

    // Contract Terms - 13 clauses with signatures
    contract_initial_1: false,
    contract_initial_2: false,
    contract_initial_3: false,
    contract_initial_4: false,
    contract_initial_5: false,
    contract_initial_6: false,
    contract_initial_7: false,
    contract_initial_8: false,
    contract_initial_9: false,
    contract_initial_10: false,
    contract_initial_11: false,
    contract_initial_12: false,
    contract_initial_13: false,
    contract_signature_1: '',
    contract_signature_2: '',
    contract_signature_3: '',
    contract_signature_4: '',
    contract_signature_5: '',
    contract_signature_6: '',
    contract_signature_7: '',
    contract_signature_8: '',
    contract_signature_9: '',
    contract_signature_10: '',
    contract_signature_11: '',
    contract_signature_12: '',
    contract_signature_13: '',

    // Parent/Guardian Information (for signing)
    parent_print_name: '',
    parent_signature: '',
    parent_signature_date: '',

    // Financially Responsible Party
    financial_print_name: '',
    financial_signature: '',
    financial_signature_date: '',
    financial_agency: '',

    // Registration Checklist
    checklist_registration_form: false,
    checklist_emergency_form: false,
    checklist_health_forms: false,
    checklist_birth_certificate: false,
    checklist_parent_handbook: false,
    checklist_waivers: false,
    checklist_child_info_page: false,
    checklist_signed_contract: false,
    checklist_offsite_form: false,
    checklist_medical_reports: false,
    checklist_debit_form: false,
    checklist_all_about_me: false,
    checklist_meal_waiver: false,
    parent_checklist_signature: '',
    parent_checklist_date: '',
    staff_witness_signature: '',
    checklist_staff_date: ''
  });

  // Fetch registration data when in edit mode (only if not provided via navigation)
  useEffect(() => {
    if (isEditMode && id && !navigationRegistrationData) {
      dispatch(fetchRegistrationPackages({ page: 1, limit: 100 }));
    }
  }, [dispatch, isEditMode, id, navigationRegistrationData]);

  useEffect(() => {
    // Use navigation data if available, otherwise use store data
    const registrationToLoad = navigationRegistrationData || existingRegistration;
    
    if (isEditMode && registrationToLoad) {
      // Map API field names to form field names
      const reg = registrationToLoad;
      
      // Debug: Log the child address fields from backend response
      console.log('Loading Registration Data - Child Address Fields:', {
        user_setting_address: reg.user_details?.user_setting?.address,
        child_address: reg.child_address,
        child_home_address: reg.child_home_address,
        child_city: reg.child_city,
        child_postal_code: reg.child_postal_code,
        all_address_fields: Object.keys(reg).filter(key => key.toLowerCase().includes('address'))
      });

      const resolvedChildDob = formatDateForAPI(
        reg.child_date_of_birth ||
        reg.child_dob ||
        reg.date_of_birth ||
        reg.user_details?.user_setting?.dob ||
        ''
      );
      
      setFormData({
        id: reg.id || id, // Include ID for edit mode
        child_first_name: reg.child_first_name || profileData?.first_name || '',
        child_last_name: reg.child_last_name || profileData?.last_name || '',
        child_dob: resolvedChildDob,
        child_age: resolvedChildDob ? calculateAge(resolvedChildDob).toString() : (reg.child_age || ''),
        child_address: reg.user_details?.user_setting?.address || reg.child_home_address || reg.child_address || '',
        child_city: reg.child_city || '',
        child_postal_code: reg.child_postal_code || '',
        // Mother fields -> parent1
        parent1_first_name: reg.mother_name ? (reg.mother_name.trim().split(/\s+/).filter(p => p)[0] || '') : (reg.parent1_first_name || ''),
        parent1_last_name: reg.mother_name ? (reg.mother_name.trim().split(/\s+/).filter(p => p).slice(1).join(' ') || '') : (reg.parent1_last_name || ''),
        parent1_phone: reg.mother_home_phone || reg.parent1_phone || '',
        parent1_cell: reg.mother_cell || reg.parent1_cell || '',
        parent1_email: reg.mother_email || reg.parent1_email || '',
        parent1_address: reg.mother_home_address || reg.parent1_address || '',
        parent1_city: reg.mother_city || reg.parent1_city || '',
        parent1_state: reg.parent1_state || '',
        parent1_zip: reg.mother_postal_code || reg.parent1_zip || '',
        parent1_work_phone: reg.mother_work_phone || reg.parent1_work_phone || '',
        parent1_work_address: reg.mother_work_address || reg.parent1_work_address || '',
        parent1_work_city: reg.mother_work_city || reg.parent1_work_city || '',
        parent1_work_postal_code: reg.mother_work_postal_code || reg.parent1_work_postal_code || '',
        parent1_relation: reg.parent1_relation || 'Mother',
        // Father fields -> parent2
        parent2_first_name: reg.father_name ? (reg.father_name.trim().split(/\s+/).filter(p => p)[0] || '') : (reg.parent2_first_name || ''),
        parent2_last_name: reg.father_name ? (reg.father_name.trim().split(/\s+/).filter(p => p).slice(1).join(' ') || '') : (reg.parent2_last_name || ''),
        parent2_phone: reg.father_home_phone || reg.parent2_phone || '',
        parent2_cell: reg.father_cell || reg.parent2_cell || '',
        parent2_email: reg.father_email || reg.parent2_email || '',
        parent2_address: reg.father_home_address || reg.parent2_address || '',
        parent2_city: reg.father_city || reg.parent2_city || '',
        parent2_state: reg.parent2_state || '',
        parent2_zip: reg.father_postal_code || reg.parent2_zip || '',
        parent2_work_phone: reg.father_work_phone || reg.parent2_work_phone || '',
        parent2_work_address: reg.father_work_address || reg.parent2_work_address || '',
        parent2_work_city: reg.father_work_city || reg.parent2_work_city || '',
        parent2_work_postal_code: reg.father_work_postal_code || reg.parent2_work_postal_code || '',
        parent2_relation: reg.parent2_relation || 'Father',
        same_address: reg.same_address || false,
        same_as_mother_address: reg.same_as_mother_address || false,
        // Emergency Contact with relationship mapping
        emergency_contact_name: reg.emergency_contact_name || '',
        emergency_contact_relation: reg.emergency_contact_relationship || reg.emergency_contact_relation || '',
        emergency_contact_address: reg.emergency_contact_home_address || reg.emergency_contact_address || '',
        emergency_contact_city: reg.emergency_contact_city || '',
        emergency_contact_postal_code: reg.emergency_contact_postal_code || '',
        emergency_contact_phone: reg.emergency_contact_phone || '',
        emergency_contact_cell: reg.emergency_contact_cell || '',
        emergency_contact_work_phone: reg.emergency_contact_work_phone || '',
        emergency_contact_work_address: reg.emergency_contact_work_address || '',
        emergency_contact_email: reg.emergency_contact_email || '',
        pediatrician_name: reg.pediatrician_name || '',
        pediatrician_phone: reg.pediatrician_phone || '',
        medical_conditions: reg.medical_conditions || '',
        allergies: reg.allergies_health_info || reg.allergies || '',
        medications: reg.current_medications || reg.medications || '',
        special_needs: reg.special_needs || '',
        previous_daycare: reg.previous_daycare || '',
        enrollment_date: reg.enrollment_date || '',
        notes: reg.notes || '',
        // Pickup persons
        pickup1_name: reg.pickup_person1_name || reg.pickup1_name || '',
        pickup1_phone: reg.pickup_person1_phone || reg.pickup1_phone || '',
        pickup1_cell: reg.pickup_person1_cell || reg.pickup1_cell || '',
        pickup1_address: reg.pickup_person1_address || reg.pickup1_address || '',
        pickup1_city: reg.pickup_person1_city || reg.pickup1_city || '',
        pickup1_postal_code: reg.pickup_person1_postal_code || reg.pickup1_postal_code || '',
        pickup2_name: reg.pickup_person2_name || reg.pickup2_name || '',
        pickup2_phone: reg.pickup_person2_phone || reg.pickup2_phone || '',
        pickup2_cell: reg.pickup_person2_cell || reg.pickup2_cell || '',
        pickup2_address: reg.pickup_person2_address || reg.pickup2_address || '',
        pickup2_city: reg.pickup_person2_city || reg.pickup2_city || '',
        pickup2_postal_code: reg.pickup_person2_postal_code || reg.pickup2_postal_code || '',
        pickup3_name: reg.pickup_person3_name || reg.pickup3_name || '',
        pickup3_phone: reg.pickup_person3_phone || reg.pickup3_phone || '',
        pickup3_cell: reg.pickup_person3_cell || reg.pickup3_cell || '',
        pickup3_address: reg.pickup_person3_address || reg.pickup3_address || '',
        pickup3_city: reg.pickup_person3_city || reg.pickup3_city || '',
        pickup3_postal_code: reg.pickup_person3_postal_code || reg.pickup3_postal_code || '',
        pickup4_name: reg.pickup_person4_name || reg.pickup4_name || '',
        pickup4_phone: reg.pickup_person4_phone || reg.pickup4_phone || '',
        pickup4_cell: reg.pickup_person4_cell || reg.pickup4_cell || '',
        pickup4_address: reg.pickup_person4_address || reg.pickup4_address || '',
        pickup4_city: reg.pickup_person4_city || reg.pickup4_city || '',
        pickup4_postal_code: reg.pickup_person4_postal_code || reg.pickup4_postal_code || '',
        dropoff_time: reg.drop_off_time || reg.dropoff_time || '',
        pickup_time: reg.pick_up_time || reg.pickup_time || '',
        parent_guardian_signature: reg.parent_guardian_signature || '',
        executive_director_signature: reg.executive_director_signature || '',
        start_date: reg.start_date || '',
        withdrawal_date: reg.withdrawal_date || '',
        emergency_auth_parent_name: reg.emergency_auth_parent_guardian || reg.emergency_auth_parent_name || '',
        emergency_auth_child_name: reg.emergency_auth_child_name || (profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : ''),
        family_physician_name: reg.family_physician_name || '',
        physician_address: reg.physician_address || '',
        physician_phone: reg.physician_phone_number || reg.physician_phone || '',
        current_medications: reg.current_medications || '',
        health_allergies: reg.allergies_health_info || reg.health_allergies || '',
        sleep_time: reg.sleep_time_night || reg.sleep_time || '',
        wake_time: reg.wake_time_morning || reg.wake_time || '',
        favourite_activity: reg.child_favourite_activity || reg.favourite_activity || '',
        emergency_auth_signature: reg.emergency_auth_signature || '',
        emergency_auth_date: reg.emergency_auth_date || '',
        agency_involvement: reg.child_related_agencies_involvement || reg.agency_involvement || '',
        vision_history: reg.vision_history_glasses || reg.vision_history || '',
        hearing_concerns: reg.hearing_concerns_audiologist || reg.hearing_concerns || '',
        diagnoses: reg.diagnoses_adhd_delays || reg.diagnoses || '',
        communicable_disease: reg.communicable_disease_history || reg.communicable_disease || '',
        eating_concerns: reg.eating_appetite_concerns || reg.eating_concerns || '',
        developmental_concerns: reg.developmental_concerns || '',
        pets_at_home: reg.pets_at_home || '',
        siblings_info: reg.siblings_family_info || reg.siblings_info || '',
        favourite_toy: reg.child_favourite_toy_activity || reg.favourite_toy || '',
        media_release_agree: reg.media_release_agree === 1 || reg.media_release_agree === '1' || reg.media_release_agree === true ? true : (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false ? false : null),
        media_release_child_name: reg.media_release_child_name_agree || reg.media_release_child_name || (profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : ''),
        media_release_date: reg.media_release_date_agree || reg.media_release_date || '',
        media_release_parent_name: reg.media_release_parent_name_agree || reg.media_release_parent_name || '',
        media_release_signature: reg.media_release_signature_agree || reg.media_release_signature || '',
        offsite_agree: reg.offsite_permission === 1 || reg.offsite_permission === '1' || reg.offsite_permission === true || reg.offsite_agree === 1 || reg.offsite_agree === '1' || reg.offsite_agree === true ? true : (reg.offsite_permission === 0 || reg.offsite_permission === '0' || reg.offsite_permission === false || reg.offsite_agree === 0 || reg.offsite_agree === '0' || reg.offsite_agree === false ? false : null),
        offsite_parent_name: reg.offsite_permission_parent_name || reg.offsite_parent_name || '',
        offsite_child_name: reg.offsite_permission_child_name || reg.offsite_child_name || (profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : ''),
        offsite_signature: reg.offsite_permission_signature || reg.offsite_signature || '',
        offsite_date: reg.offsite_permission_date || reg.offsite_date || '',
        // Contract clauses - backend may store signature URLs in contract_clauseX_initial fields
        contract_initial_1: !!(typeof reg.contract_clause1_initial === 'string' && reg.contract_clause1_initial.includes('http') ? true : (reg.contract_clause1_initial == 1 || reg.contract_initial_1 == 1 || reg.contract_initial_1 === true)),
        contract_initial_2: !!(typeof reg.contract_clause2_initial === 'string' && reg.contract_clause2_initial.includes('http') ? true : (reg.contract_clause2_initial == 1 || reg.contract_initial_2 == 1 || reg.contract_initial_2 === true)),
        contract_initial_3: !!(typeof reg.contract_clause3_initial === 'string' && reg.contract_clause3_initial.includes('http') ? true : (reg.contract_clause3_initial == 1 || reg.contract_initial_3 == 1 || reg.contract_initial_3 === true)),
        contract_initial_4: !!(typeof reg.contract_clause4_initial === 'string' && reg.contract_clause4_initial.includes('http') ? true : (reg.contract_clause4_initial == 1 || reg.contract_initial_4 == 1 || reg.contract_initial_4 === true)),
        contract_initial_5: !!(typeof reg.contract_clause5_initial === 'string' && reg.contract_clause5_initial.includes('http') ? true : (reg.contract_clause5_initial == 1 || reg.contract_initial_5 == 1 || reg.contract_initial_5 === true)),
        contract_initial_6: !!(typeof reg.contract_clause6_initial === 'string' && reg.contract_clause6_initial.includes('http') ? true : (reg.contract_clause6_initial == 1 || reg.contract_initial_6 == 1 || reg.contract_initial_6 === true)),
        contract_initial_7: !!(typeof reg.contract_clause7_initial === 'string' && reg.contract_clause7_initial.includes('http') ? true : (reg.contract_clause7_initial == 1 || reg.contract_initial_7 == 1 || reg.contract_initial_7 === true)),
        contract_initial_8: !!(typeof reg.contract_clause8_initial === 'string' && reg.contract_clause8_initial.includes('http') ? true : (reg.contract_clause8_initial == 1 || reg.contract_initial_8 == 1 || reg.contract_initial_8 === true)),
        contract_initial_9: !!(typeof reg.contract_clause9_initial === 'string' && reg.contract_clause9_initial.includes('http') ? true : (reg.contract_clause9_initial == 1 || reg.contract_initial_9 == 1 || reg.contract_initial_9 === true)),
        contract_initial_10: !!(typeof reg.contract_clause10_initial === 'string' && reg.contract_clause10_initial.includes('http') ? true : (reg.contract_clause10_initial == 1 || reg.contract_initial_10 == 1 || reg.contract_initial_10 === true)),
        contract_initial_11: !!(typeof reg.contract_clause11_initial === 'string' && reg.contract_clause11_initial.includes('http') ? true : (reg.contract_clause11_initial == 1 || reg.contract_initial_11 == 1 || reg.contract_initial_11 === true)),
        contract_initial_12: !!(typeof reg.contract_clause12_initial === 'string' && reg.contract_clause12_initial.includes('http') ? true : (reg.contract_clause12_initial == 1 || reg.contract_initial_12 == 1 || reg.contract_initial_12 === true)),
        contract_initial_13: !!(typeof reg.contract_clause13_initial === 'string' && reg.contract_clause13_initial.includes('http') ? true : (reg.contract_clause13_initial == 1 || reg.contract_initial_13 == 1 || reg.contract_initial_13 === true)),
        // Extract signature URLs from contract_clauseX_initial if they contain URLs, otherwise from contract_signature_X
        contract_signature_1: (typeof reg.contract_clause1_initial === 'string' && reg.contract_clause1_initial.includes('http')) ? reg.contract_clause1_initial : (reg.contract_signature_1 || ''),
        contract_signature_2: (typeof reg.contract_clause2_initial === 'string' && reg.contract_clause2_initial.includes('http')) ? reg.contract_clause2_initial : (reg.contract_signature_2 || ''),
        contract_signature_3: (typeof reg.contract_clause3_initial === 'string' && reg.contract_clause3_initial.includes('http')) ? reg.contract_clause3_initial : (reg.contract_signature_3 || ''),
        contract_signature_4: (typeof reg.contract_clause4_initial === 'string' && reg.contract_clause4_initial.includes('http')) ? reg.contract_clause4_initial : (reg.contract_signature_4 || ''),
        contract_signature_5: (typeof reg.contract_clause5_initial === 'string' && reg.contract_clause5_initial.includes('http')) ? reg.contract_clause5_initial : (reg.contract_signature_5 || ''),
        contract_signature_6: (typeof reg.contract_clause6_initial === 'string' && reg.contract_clause6_initial.includes('http')) ? reg.contract_clause6_initial : (reg.contract_signature_6 || ''),
        contract_signature_7: (typeof reg.contract_clause7_initial === 'string' && reg.contract_clause7_initial.includes('http')) ? reg.contract_clause7_initial : (reg.contract_signature_7 || ''),
        contract_signature_8: (typeof reg.contract_clause8_initial === 'string' && reg.contract_clause8_initial.includes('http')) ? reg.contract_clause8_initial : (reg.contract_signature_8 || ''),
        contract_signature_9: (typeof reg.contract_clause9_initial === 'string' && reg.contract_clause9_initial.includes('http')) ? reg.contract_clause9_initial : (reg.contract_signature_9 || ''),
        contract_signature_10: (typeof reg.contract_clause10_initial === 'string' && reg.contract_clause10_initial.includes('http')) ? reg.contract_clause10_initial : (reg.contract_signature_10 || ''),
        contract_signature_11: (typeof reg.contract_clause11_initial === 'string' && reg.contract_clause11_initial.includes('http')) ? reg.contract_clause11_initial : (reg.contract_signature_11 || ''),
        contract_signature_12: (typeof reg.contract_clause12_initial === 'string' && reg.contract_clause12_initial.includes('http')) ? reg.contract_clause12_initial : (reg.contract_signature_12 || ''),
        contract_signature_13: (typeof reg.contract_clause13_initial === 'string' && reg.contract_clause13_initial.includes('http')) ? reg.contract_clause13_initial : (reg.contract_signature_13 || ''),
        parent_print_name: reg.contract_parent_print_name || reg.parent_print_name || '',
        parent_signature: reg.contract_parent_signature || reg.parent_signature || '',
        parent_signature_date: reg.contract_parent_date || reg.parent_signature_date || '',
        financial_print_name: reg.financial_party_print_name || reg.financial_print_name || '',
        financial_signature: reg.financial_party_signature || reg.financial_signature || '',
        financial_signature_date: reg.financial_party_date || reg.financial_signature_date || '',
        financial_agency: reg.financial_party_agency || reg.financial_agency || '',
        // Checklist mappings
        checklist_registration_form: reg.checklist_registration_form == 1 || reg.checklist_registration_form || false,
        checklist_emergency_form: reg.checklist_emergency_form == 1 || reg.checklist_emergency_form || false,
        checklist_health_forms: reg.checklist_health_forms == 1 || reg.checklist_health_forms || false,
        checklist_birth_certificate: reg.checklist_birth_certificate == 1 || reg.checklist_birth_certificate || false,
        checklist_parent_handbook: reg.checklist_parent_handbook == 1 || reg.checklist_parent_handbook || false,
        checklist_waivers: reg.checklist_waivers == 1 || reg.checklist_waivers || false,
        checklist_child_info_page: reg.checklist_child_info_page == 1 || reg.checklist_child_info_page || false,
        checklist_signed_contract: reg.checklist_signed_contract == 1 || reg.checklist_signed_contract || false,
        checklist_offsite_form: reg.checklist_offsite_permission == 1 || reg.checklist_offsite_form || false,
        checklist_medical_reports: reg.checklist_medical_reports == 1 || reg.checklist_medical_reports || false,
        checklist_debit_form: reg.checklist_pre_authorized_debit == 1 || reg.checklist_debit_form || false,
        checklist_all_about_me: reg.checklist_all_about_me == 1 || reg.checklist_all_about_me || false,
        checklist_meal_waiver: reg.checklist_meal_waiver == 1 || reg.checklist_meal_waiver || false,
        parent_checklist_signature: reg.checklist_parent_signature || reg.parent_checklist_signature || '',
        parent_checklist_date: reg.checklist_parent_date || reg.parent_checklist_date || '',
        staff_witness_signature: reg.checklist_staff_signature || reg.staff_witness_signature || '',
        checklist_staff_date: reg.checklist_staff_date || ''
      });

      // Debug logging for contract signatures
      console.log('Loading registration data - Contract Signatures:', {
        contract_signature_1: (typeof reg.contract_clause1_initial === 'string' && reg.contract_clause1_initial.includes('http')) ? reg.contract_clause1_initial : (reg.contract_signature_1 || ''),
        contract_signature_2: (typeof reg.contract_clause2_initial === 'string' && reg.contract_clause2_initial.includes('http')) ? reg.contract_clause2_initial : (reg.contract_signature_2 || ''),
        contract_signature_3: (typeof reg.contract_clause3_initial === 'string' && reg.contract_clause3_initial.includes('http')) ? reg.contract_clause3_initial : (reg.contract_signature_3 || ''),
      });

      console.log('Loading registration data - Addresses:', {
        child_address: reg.child_address,
        mother_home_address: reg.mother_home_address,
        parent1_address: reg.parent1_address,
        father_home_address: reg.father_home_address,
        parent2_address: reg.parent2_address
      });

      // Set active pickup persons based on which ones have data
      const activePersons = [1];
      if (reg.pickup_person2_name || reg.pickup2_name) activePersons.push(2);
      if (reg.pickup_person3_name || reg.pickup3_name) activePersons.push(3);
      if (reg.pickup_person4_name || reg.pickup4_name) activePersons.push(4);
      setActivePickupPersons(activePersons);
    }
  }, [isEditMode, existingRegistration, navigationRegistrationData]);

  // Load signatures into signature pads when form data ID is set (only once on mount in edit mode)
  useEffect(() => {
    // Only load signatures once when entering edit mode, not on every formData change
    if (isEditMode && formData.id) {
      // Use a small delay to ensure signature pad refs are ready
      const loadSignatures = () => {
        // Load arrival signature
        if (formData.parent_guardian_signature && arrivalSignaturePadRef.current) {
          try {
            // Only load if pad is empty to prevent overwriting user changes
            if (arrivalSignaturePadRef.current.isEmpty()) {
              arrivalSignaturePadRef.current.fromDataURL(formData.parent_guardian_signature);
            }
          } catch (e) {
            console.log('Could not load arrival signature:', e);
          }
        }

        // Load emergency medical signature
        if (formData.emergency_auth_signature && emergencyMedicalSignaturePadRef.current) {
          try {
            if (emergencyMedicalSignaturePadRef.current.isEmpty()) {
              emergencyMedicalSignaturePadRef.current.fromDataURL(formData.emergency_auth_signature);
            }
          } catch (e) {
            console.log('Could not load emergency medical signature:', e);
          }
        }

        // Load media release signature
        if (formData.media_release_signature && mediaReleaseSignaturePadRef.current) {
          try {
            if (mediaReleaseSignaturePadRef.current.isEmpty()) {
              mediaReleaseSignaturePadRef.current.fromDataURL(formData.media_release_signature);
            }
          } catch (e) {
            console.log('Could not load media release signature:', e);
          }
        }

        // Load offsite signature
        if (formData.offsite_signature && offsiteSignaturePadRef.current) {
          try {
            if (offsiteSignaturePadRef.current.isEmpty()) {
              offsiteSignaturePadRef.current.fromDataURL(formData.offsite_signature);
            }
          } catch (e) {
            console.log('Could not load offsite signature:', e);
          }
        }

        // Load checklist signature
        if (formData.parent_checklist_signature && checklistSignaturePadRef.current) {
          try {
            if (checklistSignaturePadRef.current.isEmpty()) {
              checklistSignaturePadRef.current.fromDataURL(formData.parent_checklist_signature);
            }
          } catch (e) {
            console.log('Could not load checklist signature:', e);
          }
        }

        // Load contract parent signature
        if (formData.parent_signature && contractParentSignaturePadRef.current) {
          try {
            if (contractParentSignaturePadRef.current.isEmpty()) {
              contractParentSignaturePadRef.current.fromDataURL(formData.parent_signature);
            }
          } catch (e) {
            console.log('Could not load contract parent signature:', e);
          }
        }

        // Load contract financial signature
        if (formData.financial_signature && contractFinancialSignaturePadRef.current) {
          try {
            if (contractFinancialSignaturePadRef.current.isEmpty()) {
              contractFinancialSignaturePadRef.current.fromDataURL(formData.financial_signature);
            }
          } catch (e) {
            console.log('Could not load contract financial signature:', e);
          }
        }
      };

      // Delay signature loading to ensure refs are mounted
      const timeoutId = setTimeout(loadSignatures, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [formData.id, isEditMode]); // Only depend on ID and edit mode, not entire formData

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Helper function to format date as YYYY-MM-DD string only (no time/timezone)
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return '';
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // Otherwise, parse and format
    try {
      const date = new Date(dateValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 1) return `+${phoneNumber}`;
    if (phoneNumber.length <= 4) return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1)}`;
    if (phoneNumber.length <= 7) return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4)}`;
    return `+${phoneNumber.slice(0, 1)}-${phoneNumber.slice(1, 4)}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 11)}`;
  };

  const handleInputChange = (field, value) => {
    if (field === 'child_dob') {
      // Ensure date is stored as YYYY-MM-DD string only
      const dateOnly = formatDateForAPI(value);
      const age = calculateAge(dateOnly);
      setFormData(prev => ({
        ...prev,
        [field]: dateOnly,
        child_age: age.toString()
      }));
    } else if (field.includes('_date') || field.includes('date_')) {
      // Format all date fields to YYYY-MM-DD
      const dateOnly = formatDateForAPI(value);
      setFormData(prev => ({
        ...prev,
        [field]: dateOnly
      }));
    } else if (field.includes('phone')) {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [field]: formattedPhone
      }));
    } else if (field === 'same_address') {
      setFormData(prev => {
        if (value) {
          return {
            ...prev,
            [field]: value,
            parent2_address: prev.parent1_address,
            parent2_city: prev.parent1_city,
            parent2_state: prev.parent1_state,
            parent2_zip: prev.parent1_zip
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    } else if (field === 'same_as_mother_address') {
      setFormData(prev => {
        if (value) {
          return {
            ...prev,
            [field]: value,
            parent2_address: prev.parent1_address,
            parent2_city: prev.parent1_city,
            parent2_state: prev.parent1_state,
            parent2_zip: prev.parent1_zip,
            parent2_work_phone: prev.parent1_work_phone,
            parent2_work_address: prev.parent1_work_address,
            parent2_work_city: prev.parent1_work_city,
            parent2_work_postal_code: prev.parent1_work_postal_code
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    } else if (field.startsWith('parent1_') && formData.same_as_mother_address) {
      // If mother's address fields are updated and checkbox is checked, update father's too
      const addressFields = ['address', 'city', 'state', 'zip', 'work_phone', 'work_address', 'work_city', 'work_postal_code'];
      const fieldSuffix = field.replace('parent1_', '');
      
      if (addressFields.includes(fieldSuffix)) {
        setFormData(prev => ({
          ...prev,
          [field]: value,
          [`parent2_${fieldSuffix}`]: value
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Generic address handler that supports all address types
  const handleAddressSelect = (addressType, addressData, addressFieldMapping = {}) => {
    // Parse Google Places address components
    const parseAddressComponents = (details) => {
      const components = details?.address_components || [];
      let city = '';
      let state = '';
      let zip = '';

      components.forEach(component => {
        const types = component.types;
        if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (types.includes('postal_code')) {
          zip = component.long_name;
        }
      });

      return { city, state, zip };
    };

    // Default field mapping if not provided
    const defaultMappings = {
      'parent1': { address: 'parent1_address', city: 'parent1_city', state: 'parent1_state', zip: 'parent1_zip' },
      'parent2': { address: 'parent2_address', city: 'parent2_city', state: 'parent2_state', zip: 'parent2_zip' },
      'parent1_work': { address: 'parent1_work_address', city: 'parent1_work_city', postal: 'parent1_work_postal_code' },
      'parent2_work': { address: 'parent2_work_address', city: 'parent2_work_city', postal: 'parent2_work_postal_code' },
      'child': { address: 'child_address', city: 'child_city', postal: 'child_postal_code' },
      'emergency_contact': { address: 'emergency_contact_address', city: 'emergency_contact_city', postal: 'emergency_contact_postal_code' },
      'emergency_contact_work': { address: 'emergency_contact_work_address', city: 'emergency_contact_work_city', postal: 'emergency_contact_work_postal_code' },
      'physician': { address: 'physician_address', city: 'physician_city', postal: 'physician_postal_code' },
      'pickup1': { address: 'pickup1_address', city: 'pickup1_city', postal: 'pickup1_postal_code' },
      'pickup2': { address: 'pickup2_address', city: 'pickup2_city', postal: 'pickup2_postal_code' },
      'pickup3': { address: 'pickup3_address', city: 'pickup3_city', postal: 'pickup3_postal_code' },
      'pickup4': { address: 'pickup4_address', city: 'pickup4_city', postal: 'pickup4_postal_code' }
    };

    const mapping = addressFieldMapping || defaultMappings[addressType] || {};
    const parsed = parseAddressComponents(addressData.details);
    const updateData = { [mapping.address]: addressData.address || '' };

    // Add city if field exists
    if (mapping.city) {
      updateData[mapping.city] = parsed.city || '';
    }

    // Add state if field exists (for parent addresses)
    if (mapping.state) {
      updateData[mapping.state] = parsed.state || '';
    }

    // Add zip/postal if field exists
    if (mapping.zip) {
      updateData[mapping.zip] = parsed.zip || '';
    } else if (mapping.postal) {
      updateData[mapping.postal] = parsed.zip || '';
    }

    // Special handling for parent2 home address disabled state
    if (addressType === 'parent2' && (formData.same_address || formData.same_as_mother_address)) {
      return; // Don't update if address is disabled
    }

    setFormData(prev => ({
      ...prev,
      ...updateData
    }));
  };

  // Handle contract clause checkbox change
  const handleContractClauseChange = (index, checked) => {
    if (checked) {
      // Open signature modal when checkbox is checked
      setCurrentClauseIndex(index);
      setShowSignatureModal(true);
    } else {
      // Uncheck and clear signature
      handleInputChange(`contract_initial_${index}`, false);
      handleInputChange(`contract_signature_${index}`, '');
    }
  };

  // Save signature from modal
  const handleSaveSignature = async () => {
    if (!contractClauseSignatureRef.current || contractClauseSignatureRef.current.isEmpty()) {
      Swal.fire({
        icon: 'warning',
        title: 'Signature Required',
        text: 'Please provide your signature before saving.'
      });
      return;
    }

    try {
      setSignatureUploading(true);
      const signatureDataURL = contractClauseSignatureRef.current.toDataURL('image/png');
      const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
      const signatureFile = new File([signatureBlob], `contract_signature_${currentClauseIndex}_${Date.now()}.png`, { type: 'image/png' });
      const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');

      // Save signature and check the checkbox
      handleInputChange(`contract_signature_${currentClauseIndex}`, signatureUrl);
      handleInputChange(`contract_initial_${currentClauseIndex}`, true);

      setShowSignatureModal(false);
      setCurrentClauseIndex(null);

      Swal.fire({
        icon: 'success',
        title: 'Signature Saved',
        text: `Clause ${currentClauseIndex} signature saved successfully.`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Error',
        text: 'Failed to upload signature. Please try again.'
      });
    } finally {
      setSignatureUploading(false);
    }
  };

  // Close modal without saving
  const handleCloseSignatureModal = () => {
    setShowSignatureModal(false);
    setCurrentClauseIndex(null);
  };

  const focusInvalidField = (fieldKey) => {
    const selectorMap = {
      child_dob: '#child_dob',
      child_address: '#child_address',
      child_city: '#child_city',
      child_postal_code: '#child_postal_code',
      parent1_name: '#parent1_name',
      parent1_phone: '#parent1_phone',
      parent1_email: '#parent1_email',
      media_release_parent_name: '#media_release_parent_name',
      media_release_date: '#media_release_date',
      offsite_parent_name: '#offsite_parent_name',
      offsite_date: '#offsite_date',
      start_date: '#start_date'
    };

    const signatureCanvasMap = {
      media_release_signature: mediaReleaseSignaturePadRef.current,
      offsite_signature: offsiteSignaturePadRef.current
    };

    const signatureRef = signatureCanvasMap[fieldKey];
    if (signatureRef && signatureRef.getCanvas) {
      const canvasEl = signatureRef.getCanvas();
      if (canvasEl) {
        canvasEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          try {
            canvasEl.focus();
          } catch (e) {
            // Canvas may not always be focusable depending on browser
          }
        }, 200);
        return;
      }
    }

    const selector = selectorMap[fieldKey];
    if (!selector) return;

    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        try {
          element.focus();
        } catch (e) {
          // Ignore if element cannot be focused
        }
      }, 200);
    }
  };

  const validateForm = () => {
    // Collect all validation errors
    const errors = [];
    let firstInvalidField = null;

    const addError = (field, message) => {
      errors.push(message);
      if (!firstInvalidField) {
        firstInvalidField = field;
      }
    };

    // Email and phone regex patterns
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // ===== CHILD INFORMATION - Required fields =====
    if (!formData.child_first_name?.trim()) {
      addError('child_dob', '👶 Child: First name is required');
    }
    if (!formData.child_last_name?.trim()) {
      addError('child_dob', '👶 Child: Last name is required');
    }
    if (!formData.child_dob) {
      addError('child_dob', '👶 Child: Date of birth is required');
    }
    if (!formData.child_address?.trim()) {
      addError('child_address', '👶 Child: Address is required');
    }
    if (!formData.child_city?.trim()) {
      addError('child_city', '👶 Child: City is required');
    }
    if (!formData.child_postal_code?.trim()) {
      addError('child_postal_code', '👶 Child: Postal/ZIP code is required');
    }

    // ===== PARENT/GUARDIAN 1 INFORMATION - Required fields =====
    const parent1FullName = `${formData.parent1_first_name || ''} ${formData.parent1_last_name || ''}`.trim();
    if (!parent1FullName) {
      addError('parent1_name', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Name is required');
    }
    if (!formData.parent1_phone?.trim()) {
      addError('parent1_phone', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Phone number is required');
    } else {
      const parent1Digits = formData.parent1_phone.replace(/\D/g, '');
      if (parent1Digits.length < 10) {
        addError('parent1_phone', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Phone must have at least 10 digits');
      }
    }
    if (!formData.parent1_email?.trim()) {
      addError('parent1_email', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Email is required');
    } else if (!emailRegex.test(formData.parent1_email)) {
      addError('parent1_email', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Please enter a valid email address');
    }

    // ===== OPTIONAL PARENT 2 EMAIL VALIDATION =====
    if (formData.parent2_email?.trim() && !emailRegex.test(formData.parent2_email)) {
      addError('parent2_email', '👨‍👩‍👦 Parent/Guardian 2 (Father): Please enter a valid email address');
    }

    // ===== OPTIONAL EMERGENCY CONTACT EMAIL VALIDATION =====
    if (formData.emergency_contact_email?.trim() && !emailRegex.test(formData.emergency_contact_email)) {
      addError('emergency_contact_email', '🆘 Emergency Contact: Please enter a valid email address');
    }

    // ===== MEDIA RELEASE VALIDATION =====
    if (formData.media_release_agree) {
      if (!formData.media_release_parent_name?.trim()) {
        addError('media_release_parent_name', '📄 Media Release: Parent/guardian name is required');
      }
      if (!formData.media_release_date) {
        addError('media_release_date', '📄 Media Release: Date is required');
      }
      const hasMediaReleaseSignature = formData.media_release_signature || 
        (mediaReleaseSignaturePadRef.current && !mediaReleaseSignaturePadRef.current.isEmpty());
      if (!hasMediaReleaseSignature) {
        addError('media_release_signature', '📄 Media Release: Signature is required');
      }
    }

    // ===== OFFSITE PERMISSION VALIDATION =====
    if (formData.offsite_agree) {
      if (!formData.offsite_parent_name?.trim()) {
        addError('offsite_parent_name', '🚌 Off-Site Permission: Parent/guardian name is required');
      }
      if (!formData.offsite_date) {
        addError('offsite_date', '🚌 Off-Site Permission: Date is required');
      }
      const hasOffsiteSignature = formData.offsite_signature || 
        (offsiteSignaturePadRef.current && !offsiteSignaturePadRef.current.isEmpty());
      if (!hasOffsiteSignature) {
        addError('offsite_signature', '🚌 Off-Site Permission: Signature is required');
      }
    }

    // ===== ENROLLMENT START DATE =====
    if (!formData.start_date) {
      addError('start_date', '📅 Enrollment: Start date is required');
    }

    // ===== SHOW ALL ERRORS IF ANY FOUND =====
    if (errors.length > 0) {
      const errorHTML = `
        <div style="text-align: left; max-height: 400px; overflow-y: auto;">
          <div style="font-weight: bold; margin-bottom: 15px; font-size: 16px;">
            Found ${errors.length} validation error${errors.length !== 1 ? 's' : ''}:
          </div>
          <ul style="list-style: none; padding: 0; margin: 0;">
            ${errors.map(error => `
              <li style="
                padding: 10px; 
                margin: 5px 0; 
                background-color: #fff3cd; 
                border-left: 4px solid #ffc107; 
                border-radius: 4px;
                font-size: 14px;
              ">
                ⚠️ ${error}
              </li>
            `).join('')}
          </ul>
        </div>
      `;

      Swal.fire({
        icon: 'error',
        title: 'Form Validation Errors',
        html: errorHTML,
        width: '600px',
        confirmButtonText: 'Fix Errors',
        confirmButtonColor: '#dc3545',
        didClose: () => {
          if (firstInvalidField) {
            focusInvalidField(firstInvalidField);
          }
        }
      });

      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form - signatures will be preserved even if validation fails
    if (!validateForm()) {
      return;
    }

    // Show uploading loader
    Swal.fire({
      title: 'Processing...',
      html: 'Uploading signatures and preparing data...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const submitData = {
        ...formData,
        ...(isEditMode && { id })
      };

      // STEP 1: Upload all signatures first before calling store API
      const signatureUploads = [];

      // Arrival & Departure signature - only upload if new signature drawn, preserve existing URL
      if (arrivalSignaturePadRef.current && !arrivalSignaturePadRef.current.isEmpty()) {
        // Only upload if signature doesn't already exist as URL
        if (!submitData.parent_guardian_signature || !submitData.parent_guardian_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = arrivalSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `arrival_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.parent_guardian_signature = uploadedUrl;
            })()
          );
        }
      }

      // Emergency Medical Authorization signature - only upload if new signature drawn
      if (emergencyMedicalSignaturePadRef.current && !emergencyMedicalSignaturePadRef.current.isEmpty()) {
        if (!submitData.emergency_auth_signature || !submitData.emergency_auth_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = emergencyMedicalSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `emergency_auth_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.emergency_auth_signature = uploadedUrl;
            })()
          );
        }
      }

      // Media Release Waiver signature - only upload if new signature drawn
      if (mediaReleaseSignaturePadRef.current && !mediaReleaseSignaturePadRef.current.isEmpty()) {
        if (!submitData.media_release_signature || !submitData.media_release_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = mediaReleaseSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `media_release_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.media_release_signature = uploadedUrl;
            })()
          );
        }
      }

      // Registration Checklist signature - only upload if new signature drawn
      if (checklistSignaturePadRef.current && !checklistSignaturePadRef.current.isEmpty()) {
        if (!submitData.parent_checklist_signature || !submitData.parent_checklist_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = checklistSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `checklist_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.parent_checklist_signature = uploadedUrl;
            })()
          );
        }
      }

      // Contract Parent signature - only upload if new signature drawn
      if (contractParentSignaturePadRef.current && !contractParentSignaturePadRef.current.isEmpty()) {
        if (!submitData.parent_signature || !submitData.parent_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = contractParentSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `contract_parent_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.parent_signature = uploadedUrl;
            })()
          );
        }
      }

      // Contract Financial signature - only upload if new signature drawn
      if (contractFinancialSignaturePadRef.current && !contractFinancialSignaturePadRef.current.isEmpty()) {
        if (!submitData.financial_signature || !submitData.financial_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = contractFinancialSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `contract_financial_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.financial_signature = uploadedUrl;
            })()
          );
        }
      }

      // Off-Site Permission signature - only upload if new signature drawn
      if (offsiteSignaturePadRef.current && !offsiteSignaturePadRef.current.isEmpty()) {
        if (!submitData.offsite_signature || !submitData.offsite_signature.includes('http')) {
          signatureUploads.push(
            (async () => {
              const dataURL = offsiteSignaturePadRef.current.toDataURL('image/png');
              const blob = await fetch(dataURL).then(res => res.blob());
              const file = new File([blob], `offsite_signature_${Date.now()}.png`, { type: 'image/png' });
              const uploadedUrl = await handleImageUpload(file, 'signatures/', 'image/png');
              submitData.offsite_signature = uploadedUrl;
            })()
          );
        }
      }

      // Wait for all signature uploads to complete
      if (signatureUploads.length > 0) {
        await Promise.all(signatureUploads);
      }

      // Map form fields to API expected field names
      const formattedChildDob = formatDateForAPI(submitData.child_dob);
      const apiPayload = {
        // Keep ID for edit mode
        ...(isEditMode && { id: submitData.id }),

        // Child Information
        child_first_name: (submitData.child_first_name || '').trim(),
        child_last_name: (submitData.child_last_name || '').trim(),
        child_date_of_birth: formattedChildDob,
        child_dob: formattedChildDob,
        child_home_address: (submitData.child_address || '').trim(),
        child_city: (submitData.child_city || '').trim(),
        child_postal_code: (submitData.child_postal_code || '').trim(),

        // Mother Information (Parent 1)
        mother_name: `${(submitData.parent1_first_name || '').trim()} ${(submitData.parent1_last_name || '').trim()}`.trim(),
        mother_home_phone: (submitData.parent1_phone || '').trim(),
        mother_cell: (submitData.parent1_cell || '').trim(),
        mother_home_address: (submitData.parent1_address || '').trim(),
        mother_city: (submitData.parent1_city || '').trim(),
        mother_postal_code: (submitData.parent1_zip || '').trim(),
        mother_work_phone: (submitData.parent1_work_phone || '').trim(),
        mother_work_address: (submitData.parent1_work_address || '').trim(),
        mother_work_city: (submitData.parent1_work_city || '').trim(),
        mother_work_postal_code: (submitData.parent1_work_postal_code || '').trim(),
        mother_email: (submitData.parent1_email || '').trim(),

        // Father Information (Parent 2)
        father_name: `${(submitData.parent2_first_name || '').trim()} ${(submitData.parent2_last_name || '').trim()}`.trim(),
        father_home_phone: (submitData.parent2_phone || '').trim(),
        father_cell: (submitData.parent2_cell || '').trim(),
        father_home_address: (submitData.parent2_address || '').trim(),
        father_city: (submitData.parent2_city || '').trim(),
        father_postal_code: (submitData.parent2_zip || '').trim(),
        father_work_phone: (submitData.parent2_work_phone || '').trim(),
        father_work_address: (submitData.parent2_work_address || '').trim(),
        father_work_city: (submitData.parent2_work_city || '').trim(),
        father_work_postal_code: (submitData.parent2_work_postal_code || '').trim(),
        father_email: (submitData.parent2_email || '').trim(),

        // Emergency Contact
        emergency_contact_name: (submitData.emergency_contact_name || '').trim(),
        emergency_contact_relationship: (submitData.emergency_contact_relation || '').trim(),
        emergency_contact_home_address: (submitData.emergency_contact_address || '').trim(),
        emergency_contact_city: (submitData.emergency_contact_city || '').trim(),
        emergency_contact_postal_code: (submitData.emergency_contact_postal_code || '').trim(),
        emergency_contact_phone: (submitData.emergency_contact_phone || '').trim(),
        emergency_contact_cell: (submitData.emergency_contact_cell || '').trim(),
        emergency_contact_work_phone: (submitData.emergency_contact_work_phone || '').trim(),
        emergency_contact_work_address: (submitData.emergency_contact_work_address || '').trim(),
        emergency_contact_email: (submitData.emergency_contact_email || '').trim(),

        // Authorized Pickup Persons (map to API format)
        pickup_person_1_name: (submitData.pickup1_name || '').trim(),
        pickup_person_1_phone: (submitData.pickup1_phone || '').trim(),
        pickup_person_1_cell: (submitData.pickup1_cell || '').trim(),
        pickup_person_1_address: (submitData.pickup1_address || '').trim(),
        pickup_person_1_city: (submitData.pickup1_city || '').trim(),
        pickup_person_1_postal_code: (submitData.pickup1_postal_code || '').trim(),

        pickup_person_2_name: submitData.pickup2_name || '',
        pickup_person_2_phone: submitData.pickup2_phone || '',
        pickup_person_2_cell: submitData.pickup2_cell || '',
        pickup_person_2_address: submitData.pickup2_address || '',
        pickup_person_2_city: submitData.pickup2_city || '',
        pickup_person_2_postal_code: submitData.pickup2_postal_code || '',

        pickup_person_3_name: submitData.pickup3_name || '',
        pickup_person_3_phone: submitData.pickup3_phone || '',
        pickup_person_3_cell: submitData.pickup3_cell || '',
        pickup_person_3_address: submitData.pickup3_address || '',
        pickup_person_3_city: submitData.pickup3_city || '',
        pickup_person_3_postal_code: submitData.pickup3_postal_code || '',

        pickup_person_4_name: submitData.pickup4_name || '',
        pickup_person_4_phone: submitData.pickup4_phone || '',
        pickup_person_4_cell: submitData.pickup4_cell || '',
        pickup_person_4_address: submitData.pickup4_address || '',
        pickup_person_4_city: submitData.pickup4_city || '',
        pickup_person_4_postal_code: submitData.pickup4_postal_code || '',

        // Drop off / Pick up times
        drop_off_time: submitData.dropoff_time || '',
        pick_up_time: submitData.pickup_time || '',
        start_date: formatDateForAPI(submitData.start_date),
        withdrawal_date: formatDateForAPI(submitData.withdrawal_date),

        // Medical Information
        family_physician_name: submitData.family_physician_name || '',
        physician_address: submitData.physician_address || '',
        physician_phone_number: submitData.physician_phone || '',
        current_medications: submitData.current_medications || '',
        allergies_health_info: submitData.health_allergies || '',
        sleep_time_night: submitData.sleep_time || '',
        wake_time_morning: submitData.wake_time || '',
        child_favourite_activity: submitData.favourite_activity || '',

        // Child Information Page
        child_related_agencies_involvement: submitData.agency_involvement || '',
        vision_history_glasses: submitData.vision_history || '',
        hearing_concerns_audiologist: submitData.hearing_concerns || '',
        diagnoses_adhd_delays: submitData.diagnoses || '',
        communicable_disease_history: submitData.communicable_disease || '',
        eating_appetite_concerns: submitData.eating_concerns || '',
        developmental_concerns: submitData.developmental_concerns || '',
        pets_at_home: submitData.pets_at_home || '',
        siblings_family_info: submitData.siblings_info || '',
        child_favourite_toy_activity: submitData.favourite_toy || '',

        // Emergency Medical Authorization
        emergency_auth_parent_guardian: submitData.emergency_auth_parent_name || '',
        emergency_auth_signature: submitData.emergency_auth_signature || '',
        emergency_auth_date: formatDateForAPI(submitData.emergency_auth_date),

        // Media Release fields
        media_release_agree: submitData.media_release_agree ? 1 : 0,
        media_release_child_name_agree: submitData.media_release_agree ? submitData.media_release_child_name : '',
        media_release_date_agree: submitData.media_release_agree ? formatDateForAPI(submitData.media_release_date) : '',
        media_release_parent_name_agree: submitData.media_release_agree ? submitData.media_release_parent_name : '',
        media_release_signature_agree: submitData.media_release_agree ? submitData.media_release_signature : '',
        media_release_child_name_disagree: !submitData.media_release_agree ? submitData.media_release_child_name : '',
        media_release_date_disagree: !submitData.media_release_agree ? formatDateForAPI(submitData.media_release_date) : '',
        media_release_parent_name_disagree: !submitData.media_release_agree ? submitData.media_release_parent_name : '',
        media_release_signature_disagree: !submitData.media_release_agree ? submitData.media_release_signature : '',

        // Off-Site Permission
        offsite_permission: submitData.offsite_agree ? 1 : 0,
        offsite_permission_child_name: (submitData.offsite_child_name || '').trim(),
        offsite_permission_parent_name: (submitData.offsite_parent_name || '').trim(),
        offsite_permission_signature: submitData.offsite_signature || '',
        offsite_permission_date: formatDateForAPI(submitData.offsite_date),

        // Contract Clauses (1-13) - Send signature URL in contract_clause_X_initial if signature exists, otherwise send 1/0
        contract_clause_1_initial: submitData.contract_signature_1 ? submitData.contract_signature_1 : (submitData.contract_initial_1 ? 1 : 0),
        contract_clause_2_initial: submitData.contract_signature_2 ? submitData.contract_signature_2 : (submitData.contract_initial_2 ? 1 : 0),
        contract_clause_3_initial: submitData.contract_signature_3 ? submitData.contract_signature_3 : (submitData.contract_initial_3 ? 1 : 0),
        contract_clause_4_initial: submitData.contract_signature_4 ? submitData.contract_signature_4 : (submitData.contract_initial_4 ? 1 : 0),
        contract_clause_5_initial: submitData.contract_signature_5 ? submitData.contract_signature_5 : (submitData.contract_initial_5 ? 1 : 0),
        contract_clause_6_initial: submitData.contract_signature_6 ? submitData.contract_signature_6 : (submitData.contract_initial_6 ? 1 : 0),
        contract_clause_7_initial: submitData.contract_signature_7 ? submitData.contract_signature_7 : (submitData.contract_initial_7 ? 1 : 0),
        contract_clause_8_initial: submitData.contract_signature_8 ? submitData.contract_signature_8 : (submitData.contract_initial_8 ? 1 : 0),
        contract_clause_9_initial: submitData.contract_signature_9 ? submitData.contract_signature_9 : (submitData.contract_initial_9 ? 1 : 0),
        contract_clause_10_initial: submitData.contract_signature_10 ? submitData.contract_signature_10 : (submitData.contract_initial_10 ? 1 : 0),
        contract_clause_11_initial: submitData.contract_signature_11 ? submitData.contract_signature_11 : (submitData.contract_initial_11 ? 1 : 0),
        contract_clause_12_initial: submitData.contract_signature_12 ? submitData.contract_signature_12 : (submitData.contract_initial_12 ? 1 : 0),
        contract_clause_13_initial: submitData.contract_signature_13 ? submitData.contract_signature_13 : (submitData.contract_initial_13 ? 1 : 0),

        // Contract Clause Signatures (1-13) - Also send in separate fields for compatibility
        contract_signature_1: submitData.contract_signature_1 || '',
        contract_signature_2: submitData.contract_signature_2 || '',
        contract_signature_3: submitData.contract_signature_3 || '',
        contract_signature_4: submitData.contract_signature_4 || '',
        contract_signature_5: submitData.contract_signature_5 || '',
        contract_signature_6: submitData.contract_signature_6 || '',
        contract_signature_7: submitData.contract_signature_7 || '',
        contract_signature_8: submitData.contract_signature_8 || '',
        contract_signature_9: submitData.contract_signature_9 || '',
        contract_signature_10: submitData.contract_signature_10 || '',
        contract_signature_11: submitData.contract_signature_11 || '',
        contract_signature_12: submitData.contract_signature_12 || '',
        contract_signature_13: submitData.contract_signature_13 || '',

        // Contract Parent/Guardian Signature
        contract_parent_print_name: submitData.parent_print_name || '',
        contract_parent_signature: submitData.parent_signature || '',
        contract_parent_date: formatDateForAPI(submitData.parent_signature_date),

        // Contract Financial Party Signature
        financial_party_print_name: submitData.financial_print_name || '',
        financial_party_signature: submitData.financial_signature || '',
        financial_party_date: formatDateForAPI(submitData.financial_signature_date),
        financial_party_agency: submitData.financial_agency || '',

        // Arrival & Departure Signatures
        parent_guardian_signature: submitData.parent_guardian_signature || '',
        executive_director_signature: submitData.executive_director_signature || '',

        // Registration Checklist
        checklist_registration_form: submitData.checklist_registration_form ? 1 : 0,
        checklist_emergency_form: submitData.checklist_emergency_form ? 1 : 0,
        checklist_health_forms: submitData.checklist_health_forms ? 1 : 0,
        checklist_birth_certificate: submitData.checklist_birth_certificate ? 1 : 0,
        checklist_parent_handbook: submitData.checklist_parent_handbook ? 1 : 0,
        checklist_waivers: submitData.checklist_waivers ? 1 : 0,
        checklist_child_info_page: submitData.checklist_child_info_page ? 1 : 0,
        checklist_signed_contract: submitData.checklist_signed_contract ? 1 : 0,
        checklist_offsite_permission: submitData.checklist_offsite_form ? 1 : 0,
        checklist_medical_reports: submitData.checklist_medical_reports ? 1 : 0,
        checklist_pre_authorized_debit: submitData.checklist_debit_form ? 1 : 0,
        checklist_all_about_me: submitData.checklist_all_about_me ? 1 : 0,
        checklist_meal_waiver: submitData.checklist_meal_waiver ? 1 : 0,
        checklist_parent_signature: submitData.parent_checklist_signature || '',
        checklist_parent_date: formatDateForAPI(submitData.parent_checklist_date),
        checklist_staff_signature: submitData.staff_witness_signature || '',
        checklist_staff_date: formatDateForAPI(submitData.checklist_staff_date)
      };

      console.log('Submitting Registration - Full Child Address Data:', {
        child_home_address: apiPayload.child_home_address,
        child_city: apiPayload.child_city,
        child_postal_code: apiPayload.child_postal_code,
        formData_child_address: formData.child_address,
        submitData_child_address: submitData.child_address
      });
      
      console.log('Full API Payload - Child Section:', {
        child_first_name: apiPayload.child_first_name,
        child_last_name: apiPayload.child_last_name,
        child_date_of_birth: apiPayload.child_date_of_birth,
        child_home_address: apiPayload.child_home_address,
        child_city: apiPayload.child_city,
        child_postal_code: apiPayload.child_postal_code
      });

      // STEP 2: Update loader message
      Swal.update({
        html: 'Submitting registration data...'
      });

      // STEP 3: Call store API with all uploaded signature URLs
      const result = await dispatch(submitChildRegistration(apiPayload)).unwrap();

      // Log the response from backend
      console.log('Backend Response after save:', result);
      console.log('Backend Response - Child Address:', {
        child_address: result?.data?.child_address || result?.child_address,
        child_home_address: result?.data?.child_home_address || result?.child_home_address,
        response_keys: result?.data ? Object.keys(result.data) : Object.keys(result || {})
      });

      // STEP 4: Close the loader
      Swal.close();

      // STEP 5: Show success message only on successful submission
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: isEditMode ? 'Child registration updated successfully.' : 'Child registration submitted successfully.',
        timer: 2000,
        showConfirmButton: false
      });

      // STEP 6: Clear all signature pads only after successful submission
      if (arrivalSignaturePadRef.current) arrivalSignaturePadRef.current.clear();
      if (emergencyMedicalSignaturePadRef.current) emergencyMedicalSignaturePadRef.current.clear();
      if (mediaReleaseSignaturePadRef.current) mediaReleaseSignaturePadRef.current.clear();
      if (checklistSignaturePadRef.current) checklistSignaturePadRef.current.clear();
      if (contractParentSignaturePadRef.current) contractParentSignaturePadRef.current.clear();
      if (contractFinancialSignaturePadRef.current) contractFinancialSignaturePadRef.current.clear();
      if (offsiteSignaturePadRef.current) offsiteSignaturePadRef.current.clear();

      // Reset form or navigate
      if (onSubmit) {
        // Call onSubmit callback (getProfileDetails in user-profile.js) which will fetch registration data
        onSubmit(formData);
      } else {
        // Only fetch registration packages when not in profile view
        await dispatch(fetchRegistrationPackages({ page: 1, limit: 100 })).unwrap();
        navigate('/dashboard/child-registration/list', { replace: true });
      }
    } catch (error) {
      // Close any open loader
      Swal.close();
      
      console.error('Form submission error:', error);
      
      // Parse validation errors if present
      let errorTitle = 'Submission Failed';
      let errorMessage = 'Failed to submit the form. Please try again.';
      let errorIcon = 'error';
      
      if (error && typeof error === 'string') {
        errorMessage = error;
      } else if (error?.errors && Array.isArray(error.errors)) {
        // Handle 422 validation errors
        errorTitle = 'Validation Error';
        errorIcon = 'warning';
        errorMessage = error.errors.map(err => 
          `<strong>${err.field}:</strong> ${err.message}`
        ).join('<br>');
      } else if (error?.message) {
        errorMessage = error.message;
        // Check for specific error types
        if (error.message.includes('Network') || error.message.includes('network')) {
          errorTitle = 'Network Error';
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorTitle = 'Request Timeout';
          errorMessage = 'The request took too long to complete. Please try again.';
        } else if (error.message.includes('signature') || error.message.includes('upload')) {
          errorTitle = 'Upload Error';
          errorMessage = 'Failed to upload one or more signatures. Please check your signatures and try again.';
        }
      }
      
      Swal.fire({
        icon: errorIcon,
        title: errorTitle,
        html: errorMessage + '<br><br><small style="color: #666;">Note: Your signatures have been preserved. Please correct the errors and submit again.</small>',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
        allowOutsideClick: true,
        allowEscapeKey: true
      });
      
      // Note: Signature pads are NOT cleared on error - they remain intact for resubmission
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/dashboard/child-registration/list');
    }
  };

  return (
    <>
      <style>{`
        .form-control, .form-select {
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          padding: 10px 15px;
          transition: all 0.3s ease;
        }
        .form-control:focus, .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
        }
        .form-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
        }
        .bg-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        }
      `}</style>

      {/* Main Form Section */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="text-center mb-4 pb-4 border-bottom">
            <h3 className="text-dark mb-2 fw-bold">Registration Form</h3>
            <p className="text-muted mb-0">Please fill in all required information to complete the registration</p>
          </div>

          {remarks && (
            <div className="alert alert-info border-0" style={{ backgroundColor: '#e7f3ff', borderLeft: '4px solid #2196F3' }}>
              <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
              {remarks}
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Child Information Section */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">child_care</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Child Information</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.child_first_name}
                      readOnly
                      disabled
                      placeholder="Auto-filled from profile"
                      style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                    />
                    <Form.Text className="text-muted">
                      This field is auto-populated from your profile name
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.child_last_name}
                      readOnly
                      disabled
                      placeholder="Auto-filled from profile"
                      style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                    />
                    <Form.Text className="text-muted">
                      This field is auto-populated from your profile name
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_dob"
                      type="date"
                      value={formData.child_dob}
                      onChange={(e) => handleInputChange('child_dob', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.child_age}
                      readOnly
                      placeholder="Calculated from DOB"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                    <AddressAutocomplete
                      key={`child-address-${formData.id || 'new'}`}
                      inputId="child_address"
                      onSelect={(data) => handleAddressSelect('child', data)}
                      defaultAddress={formData.child_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_city"
                      type="text"
                      value={formData.child_city}
                      onChange={(e) => handleInputChange('child_city', e.target.value)}
                      placeholder="Enter city"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_postal_code"
                      type="text"
                      value={formData.child_postal_code}
                      onChange={(e) => handleInputChange('child_postal_code', e.target.value)}
                      placeholder="Enter postal/ZIP code"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Parent/Guardian Information */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">family_restroom</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Parent/Guardian Information</h5>
              </div>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mother's Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_name"
                      type="text"
                      value={formData.parent1_last_name !== null && formData.parent1_last_name !== undefined ? 
                        `${formData.parent1_first_name || ''} ${formData.parent1_last_name}` : 
                        (formData.parent1_first_name || '')}
                      onChange={(e) => {
                        const fullName = e.target.value;
                        
                        // Check if there's a space in the input
                        if (fullName.includes(' ')) {
                          // Split on first space only
                          const firstSpaceIndex = fullName.indexOf(' ');
                          const firstName = fullName.substring(0, firstSpaceIndex);
                          const lastName = fullName.substring(firstSpaceIndex + 1);
                          handleInputChange('parent1_first_name', firstName);
                          handleInputChange('parent1_last_name', lastName);
                        } else {
                          // No space yet - everything goes to first name
                          handleInputChange('parent1_first_name', fullName);
                          handleInputChange('parent1_last_name', null);
                        }
                      }}
                      onBlur={(e) => {
                        // On blur, clean up extra spaces
                        const trimmedName = e.target.value.trim();
                        const parts = trimmedName.split(/\s+/).filter(p => p);
                        
                        if (parts.length === 0) {
                          handleInputChange('parent1_first_name', '');
                          handleInputChange('parent1_last_name', '');
                        } else if (parts.length === 1) {
                          // Single name - duplicate for submission
                          handleInputChange('parent1_first_name', parts[0]);
                          handleInputChange('parent1_last_name', parts[0]);
                        } else {
                          handleInputChange('parent1_first_name', parts[0]);
                          handleInputChange('parent1_last_name', parts.slice(1).join(' '));
                        }
                      }}
                      placeholder="Enter mother's full name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Phone <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_phone"
                      type="text"
                      value={formData.parent1_phone}
                      onChange={(e) => handleInputChange('parent1_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_cell}
                      onChange={(e) => handleInputChange('parent1_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent1-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect(1, data)}
                      defaultAddress={formData.parent1_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_city}
                      onChange={(e) => handleInputChange('parent1_city', e.target.value)}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_zip}
                      onChange={(e) => handleInputChange('parent1_zip', e.target.value)}
                      placeholder="Postal/ZIP Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_phone}
                      onChange={(e) => handleInputChange('parent1_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent1-work-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect('parent1_work', data)}
                      defaultAddress={formData.parent1_work_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_city}
                      onChange={(e) => handleInputChange('parent1_work_city', e.target.value)}
                      placeholder="Work city"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_postal_code}
                      onChange={(e) => handleInputChange('parent1_work_postal_code', e.target.value)}
                      placeholder="Work postal/ZIP code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (for communication and SeeSaw) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_email"
                      type="email"
                      value={formData.parent1_email}
                      onChange={(e) => handleInputChange('parent1_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Father's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_last_name !== null && formData.parent2_last_name !== undefined ? 
                        `${formData.parent2_first_name || ''} ${formData.parent2_last_name}` : 
                        (formData.parent2_first_name || '')}
                      onChange={(e) => {
                        const fullName = e.target.value;
                        
                        // Check if there's a space in the input
                        if (fullName.includes(' ')) {
                          // Split on first space only
                          const firstSpaceIndex = fullName.indexOf(' ');
                          const firstName = fullName.substring(0, firstSpaceIndex);
                          const lastName = fullName.substring(firstSpaceIndex + 1);
                          handleInputChange('parent2_first_name', firstName);
                          handleInputChange('parent2_last_name', lastName);
                        } else {
                          // No space yet - everything goes to first name
                          handleInputChange('parent2_first_name', fullName);
                          handleInputChange('parent2_last_name', null);
                        }
                      }}
                      onBlur={(e) => {
                        // On blur, clean up extra spaces
                        const trimmedName = e.target.value.trim();
                        const parts = trimmedName.split(/\s+/).filter(p => p);
                        
                        if (parts.length === 0) {
                          handleInputChange('parent2_first_name', '');
                          handleInputChange('parent2_last_name', '');
                        } else if (parts.length === 1) {
                          // Single name - duplicate for submission
                          handleInputChange('parent2_first_name', parts[0]);
                          handleInputChange('parent2_last_name', parts[0]);
                        } else {
                          handleInputChange('parent2_first_name', parts[0]);
                          handleInputChange('parent2_last_name', parts.slice(1).join(' '));
                        }
                      }}
                      placeholder="Enter father's full name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <div style={{
                      backgroundColor: '#f0f8ff',
                      border: '1px solid #d4e4f7',
                      borderRadius: '8px',
                      padding: '12px 15px',
                      display: 'inline-block'
                    }}>
                      <Form.Check
                        type="checkbox"
                        id="same-as-mother-address"
                        label={<span style={{ fontWeight: '500', fontSize: '13px', color: '#5a6c7d' }}>Same as Mother's Address</span>}
                        checked={formData.same_as_mother_address}
                        onChange={(e) => handleInputChange('same_as_mother_address', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_phone}
                      onChange={(e) => handleInputChange('parent2_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_cell}
                      onChange={(e) => handleInputChange('parent2_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent2-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect(2, data)}
                      defaultAddress={formData.parent2_address}
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_city}
                      onChange={(e) => handleInputChange('parent2_city', e.target.value)}
                      placeholder="City"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_zip}
                      onChange={(e) => handleInputChange('parent2_zip', e.target.value)}
                      placeholder="Postal/ZIP Code"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_phone}
                      onChange={(e) => handleInputChange('parent2_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent2-work-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect('parent2_work', data)}
                      defaultAddress={formData.parent2_work_address}
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_city}
                      onChange={(e) => handleInputChange('parent2_work_city', e.target.value)}
                      placeholder="Work city"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_postal_code}
                      onChange={(e) => handleInputChange('parent2_work_postal_code', e.target.value)}
                      placeholder="Work postal/ZIP code"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (for communication and SeeSaw)</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.parent2_email}
                      onChange={(e) => handleInputChange('parent2_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Emergency Contact Information */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">contact_emergency</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Emergency Contact (Not a Parent)</h5>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name & Relationship</Form.Label>
                    <Form.Control
                      type="text"
                      value={`${formData.emergency_contact_name}${formData.emergency_contact_relation ? ' - ' + formData.emergency_contact_relation : ''}`.trim()}
                      onChange={(e) => {
                        const value = e.target.value;
                        const parts = value.split(' - ');
                        const name = parts[0] || '';
                        const relation = parts[1] || '';
                        handleInputChange('emergency_contact_name', name);
                        handleInputChange('emergency_contact_relation', relation);
                      }}
                      placeholder="Full name - Relationship (e.g., John Doe - Uncle)"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`emergency-contact-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect('emergency_contact', data)}
                      defaultAddress={formData.emergency_contact_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_city}
                      onChange={(e) => handleInputChange('emergency_contact_city', e.target.value)}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_postal_code}
                      onChange={(e) => handleInputChange('emergency_contact_postal_code', e.target.value)}
                      placeholder="Postal/ZIP Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_cell}
                      onChange={(e) => handleInputChange('emergency_contact_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_work_phone}
                      onChange={(e) => handleInputChange('emergency_contact_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`emergency-work-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect('emergency_contact_work', data)}
                      defaultAddress={formData.emergency_contact_work_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.emergency_contact_email}
                      onChange={(e) => handleInputChange('emergency_contact_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Authorized Pick-Up List */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                  <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                    <i className="material-symbols-outlined">group_add</i>
                  </div>
                  <h5 className="mb-0 fw-bold text-dark">Authorized Pick-Up List</h5>
                </div>
                {activePickupPersons.length < 4 && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      const nextNum = Math.max(...activePickupPersons) + 1;
                      if (nextNum <= 4) {
                        setActivePickupPersons([...activePickupPersons, nextNum]);
                      }
                    }}
                  >
                    <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>add_circle</i>
                    Add Person
                  </Button>
                )}
              </div>
              <p className="text-muted mb-4">List individuals (besides parents and emergency contact) authorized to pick up your child. Include full name, relationship, and phone number.</p>

              {activePickupPersons.map((num) => (
                <div key={num} className="mb-4 p-3 position-relative" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #dee2e6' }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="text-secondary mb-0">Authorized Person #{num}</h6>
                    {activePickupPersons.length > 1 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => {
                          // Clear the data for this person
                          handleInputChange(`pickup${num}_name`, '');
                          handleInputChange(`pickup${num}_phone`, '');
                          handleInputChange(`pickup${num}_cell`, '');
                          handleInputChange(`pickup${num}_address`, '');
                          handleInputChange(`pickup${num}_city`, '');
                          handleInputChange(`pickup${num}_postal_code`, '');
                          // Remove this person from the active list
                          setActivePickupPersons(activePickupPersons.filter(n => n !== num));
                        }}
                      >
                        <i className="material-symbols-outlined" style={{ fontSize: '20px', verticalAlign: 'middle' }}>delete</i>
                        Remove
                      </Button>
                    )}
                  </div>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_name`]}
                          onChange={(e) => handleInputChange(`pickup${num}_name`, e.target.value)}
                          placeholder="Full name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_phone`]}
                          onChange={(e) => handleInputChange(`pickup${num}_phone`, e.target.value)}
                          placeholder="+1-XXX-XXX-XXXX"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cell</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_cell`]}
                          onChange={(e) => handleInputChange(`pickup${num}_cell`, e.target.value)}
                          placeholder="+1-XXX-XXX-XXXX"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <AddressAutocomplete
                          key={`pickup${num}-address-${formData.id || 'new'}`}
                          onSelect={(data) => handleAddressSelect(`pickup${num}`, data)}
                          defaultAddress={formData[`pickup${num}_address`]}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_city`]}
                          onChange={(e) => handleInputChange(`pickup${num}_city`, e.target.value)}
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postal/ZIP Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_postal_code`]}
                          onChange={(e) => handleInputChange(`pickup${num}_postal_code`, e.target.value)}
                          placeholder="Postal/ZIP Code"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            {/* Arrival and Departure */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">schedule</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Arrival and Departure</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Drop-off Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.dropoff_time}
                      onChange={(e) => handleInputChange('dropoff_time', e.target.value)}
                    />
                    <Form.Text className="text-muted">Fixed hours required for full-day attendance</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pick-up Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.pickup_time}
                      onChange={(e) => handleInputChange('pickup_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent/Guardian Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={arrivalSignaturePadRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => arrivalSignaturePadRef.current?.clear()}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Withdrawal Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.withdrawal_date}
                      onChange={(e) => handleInputChange('withdrawal_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Emergency Medical Authorization Form */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">local_hospital</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Emergency Medical Authorization Form</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent/Guardian Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_auth_parent_name}
                      onChange={(e) => handleInputChange('emergency_auth_parent_name', e.target.value)}
                      placeholder="Parent/Guardian name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_auth_child_name}
                      readOnly
                      disabled
                      placeholder="Auto-filled from profile"
                      style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                    />
                    <Form.Text className="text-muted">
                      This field is auto-populated from your profile name
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="mb-4 p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <p className="mb-0">
                  I, <strong>{formData.emergency_auth_parent_name || '____________________'}</strong>, parent/guardian of <strong>{formData.emergency_auth_child_name || '____________________'}</strong>, authorize Harmony Arts Daycare and Education Centre to obtain emergency medical treatment if necessary.
                </p>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Family Physician's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.family_physician_name}
                      onChange={(e) => handleInputChange('family_physician_name', e.target.value)}
                      placeholder="Doctor's name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Physician Address</Form.Label>
                    <AddressAutocomplete
                      key={`physician-address-${formData.id || 'new'}`}
                      onSelect={(data) => handleAddressSelect('physician', data)}
                      defaultAddress={formData.physician_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Physician Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.physician_phone}
                      onChange={(e) => handleInputChange('physician_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Medications</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.current_medications}
                      onChange={(e) => handleInputChange('current_medications', e.target.value)}
                      placeholder="List current medications"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Allergies/Relevant Health Info</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.health_allergies}
                      onChange={(e) => handleInputChange('health_allergies', e.target.value)}
                      placeholder="List allergies and relevant health information"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sleep Time (Night)</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.sleep_time}
                      onChange={(e) => handleInputChange('sleep_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Wake Time (Morning)</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.wake_time}
                      onChange={(e) => handleInputChange('wake_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Favourite Activity</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.favourite_activity}
                      onChange={(e) => handleInputChange('favourite_activity', e.target.value)}
                      placeholder="Favourite activity"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={emergencyMedicalSignaturePadRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => emergencyMedicalSignaturePadRef.current?.clear()}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.emergency_auth_date}
                      onChange={(e) => handleInputChange('emergency_auth_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Child Information Page */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">child_care</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Child Information Page</h5>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Involvement with Child-Related Agencies (e.g., speech therapy)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.agency_involvement}
                      onChange={(e) => handleInputChange('agency_involvement', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vision History / Glasses Usage</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.vision_history}
                      onChange={(e) => handleInputChange('vision_history', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hearing Concerns / Audiologist Visits</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.hearing_concerns}
                      onChange={(e) => handleInputChange('hearing_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Any Diagnoses (e.g., ADHD, delays)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.diagnoses}
                      onChange={(e) => handleInputChange('diagnoses', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Communicable Disease History</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.communicable_disease}
                      onChange={(e) => handleInputChange('communicable_disease', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Eating/Appetite Concerns</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.eating_concerns}
                      onChange={(e) => handleInputChange('eating_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Developmental Concerns</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.developmental_concerns}
                      onChange={(e) => handleInputChange('developmental_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pets at Home</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.pets_at_home}
                      onChange={(e) => handleInputChange('pets_at_home', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Siblings / Family Info</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.siblings_info}
                      onChange={(e) => handleInputChange('siblings_info', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Favourite Toy or Activity</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.favourite_toy}
                      onChange={(e) => handleInputChange('favourite_toy', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Media Release Waiver */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">photo_camera</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Media Release Waiver</h5>
              </div>
              <p className="text-muted mb-4">Harmony Arts Daycare and Education Centre may use photos/videos on Instagram, SeeSaw, and through media partners (e.g., Brant News, The Expositor, Rogers).</p>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="radio"
                      name="media_release"
                      label="I AGREE to media release"
                      checked={formData.media_release_agree === true || formData.media_release_agree === 1 || formData.media_release_agree === '1'}
                      onChange={() => handleInputChange('media_release_agree', true)}
                    />
                    <Form.Check
                      style={{ marginLeft: "20px" }}
                      type="radio"
                      name="media_release"
                      label="I DISAGREE with media release"
                      checked={formData.media_release_agree === false || formData.media_release_agree === 0 || formData.media_release_agree === '0'}
                      onChange={() => handleInputChange('media_release_agree', false)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {(formData.media_release_agree === true || formData.media_release_agree === 1 || formData.media_release_agree === '1') && (
                <div className="mb-4 p-3" style={{ backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #28a745' }}>
                  <h6 className="text-success mb-3">If you AGREE:</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_child_name}
                          readOnly
                          disabled
                          placeholder="Auto-filled from profile"
                          style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                        />
                        <Form.Text className="text-muted">
                          This field is auto-populated from your profile name
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="media_release_date"
                          type="date"
                          value={formData.media_release_date}
                          onChange={(e) => handleInputChange('media_release_date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="media_release_parent_name"
                          type="text"
                          value={formData.media_release_parent_name}
                          onChange={(e) => handleInputChange('media_release_parent_name', e.target.value)}
                          placeholder="Parent's name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                        <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                          <SignatureCanvas
                            ref={mediaReleaseSignaturePadRef}
                            canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '100%' }
                            }}
                          />
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => mediaReleaseSignaturePadRef.current?.clear()}
                          type="button"
                        >
                          Clear Signature
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {(formData.media_release_agree === false || formData.media_release_agree === 0 || formData.media_release_agree === '0') && (
                <div className="mb-4 p-3" style={{ backgroundColor: '#f8d7da', borderRadius: '8px', border: '1px solid #dc3545' }}>
                  <h6 className="text-danger mb-3">If you DISAGREE:</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_child_name}
                          readOnly
                          disabled
                          placeholder="Auto-filled from profile"
                          style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                        />
                        <Form.Text className="text-muted">
                          This field is auto-populated from your profile name
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.media_release_date}
                          onChange={(e) => handleInputChange('media_release_date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_parent_name}
                          onChange={(e) => handleInputChange('media_release_parent_name', e.target.value)}
                          placeholder="Parent's name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                        <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                          <SignatureCanvas
                            ref={mediaReleaseSignaturePadRef}
                            canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '100%' }
                            }}
                          />
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => mediaReleaseSignaturePadRef.current?.clear()}
                          type="button"
                        >
                          Clear Signature
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Off-Site Permission Form */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">directions_walk</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Off-Site Permission Form</h5>
              </div>
              
              <div className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="offsite-permission-agree"
                  checked={formData.offsite_agree}
                  onChange={(e) => handleInputChange('offsite_agree', e.target.checked)}
                  label={
                    <span>
                      <strong>I give permission</strong> for Harmony Arts Daycare and Education Centre staff to take my child on supervised off-site walks around the neighbourhood.
                    </span>
                  }
                />
              </div>
              
              {formData.offsite_agree && (
                <div>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent/Guardian Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="offsite_parent_name"
                          type="text"
                          value={formData.offsite_parent_name}
                          onChange={(e) => handleInputChange('offsite_parent_name', e.target.value)}
                          placeholder="Parent/Guardian name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.offsite_child_name}
                      readOnly
                      disabled
                      placeholder="Auto-filled from profile"
                      style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                    />
                    <Form.Text className="text-muted">
                      This field is auto-populated from your profile name
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="mb-4 p-3" style={{ backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #28a745' }}>
                <p className="mb-0">
                  I, <strong>{formData.offsite_parent_name || '____________________'}</strong>, give permission for Harmony Arts Daycare and Education Centre staff to take my child, <strong>{formData.offsite_child_name || '____________________'}</strong>, on supervised off-site walks around the neighbourhood.
                </p>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={offsiteSignaturePadRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => offsiteSignaturePadRef.current?.clear()}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="offsite_date"
                      type="date"
                      value={formData.offsite_date}
                      onChange={(e) => handleInputChange('offsite_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
                </div>
              )}
            </div>

            {/* Contract Terms and Conditions */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">gavel</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Contract Terms and Conditions <span className="text-danger">*</span></h5>
              </div>
              <p className="text-muted mb-4">Please read each clause carefully. Click the checkbox to sign each clause individually:</p>
              {[
                "I have reviewed and understood the Parent Policies Handbook (PPH).",
                "I agree to comply with all policies, including health, safety, and ministry regulations.",
                "I acknowledge that custody/visitation disputes require valid court documentation.",
                "I understand that while parental concerns are welcomed, certain policies are not subject to parental discretion.",
                "All required enrollment forms and documents will be submitted before the child's start date.",
                "I agree that tuition is due on the 1st of every month, and non-payment may result in suspended attendance.",
                "I grant permission for emergency medical care for my child if necessary.",
                "I agree to pay all extra-curricular activity fees in advance.",
                "I understand that 30 days' written notice is required for any withdrawal or schedule change.",
                "I understand that fees are non-refundable for absences, illness, or statutory holidays.",
                "I acknowledge that enrolment may be terminated for delinquent accounts, and I am responsible for any associated collection or legal fees.",
                "I am responsible for ensuring that all authorized persons follow pick-up procedures.",
                "I understand non-compliance with these terms may result in contract termination."
              ].map((clause, index) => {
                const clauseNumber = index + 1;
                const hasSignature = formData[`contract_signature_${clauseNumber}`];
                const isChecked = formData[`contract_initial_${clauseNumber}`];

                return (
                  <div key={index} className="mb-3 p-3 border rounded" style={{ backgroundColor: isChecked ? '#e8f5e9' : '#f8f9fa', borderColor: isChecked ? '#4caf50' : '#dee2e6' }}>
                    <Row className="align-items-center">
                      <Col md={hasSignature ? 9 : 12}>
                        <Form.Check
                          type="checkbox"
                          id={`contract-${clauseNumber}`}
                          label={`${clauseNumber}. ${clause} *`}
                          checked={isChecked}
                          onChange={(e) => handleContractClauseChange(clauseNumber, e.target.checked)}
                          disabled={hasSignature}
                        />
                        {hasSignature && (
                          <div className="mt-2">
                            <Badge bg="success" className="me-2">
                              <i className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>check_circle</i>
                              {' '}Signed
                            </Badge>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => {
                                handleInputChange(`contract_initial_${clauseNumber}`, false);
                                handleInputChange(`contract_signature_${clauseNumber}`, '');
                              }}
                            >
                              <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>delete</i>
                              Remove Signature
                            </Button>
                          </div>
                        )}
                      </Col>
                      {hasSignature && (
                        <Col md={3} className="text-end">
                          <div
                            className="border rounded p-2 bg-white"
                            style={{
                              width: '120px',
                              height: '60px',
                              display: 'inline-block',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            <img
                              src={hasSignature}
                              alt={`Signature ${clauseNumber}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                );
              })}

              {/* Summary */}
              <div className="mt-4 p-3 border rounded" style={{ backgroundColor: '#e3f2fd' }}>
                <Row className="align-items-center">
                  <Col md={8}>
                    <strong>
                      <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
                      Signed Clauses: {Object.keys(formData).filter(key => key.startsWith('contract_initial_') && formData[key]).length} / 13
                    </strong>
                  </Col>
                  <Col md={4} className="text-end">
                    {Object.keys(formData).filter(key => key.startsWith('contract_initial_') && formData[key]).length === 13 ? (
                      <Badge bg="success" className="p-2">
                        <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>verified</i>
                        {' '}All Clauses Signed
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="p-2">
                        <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>pending</i>
                        {' '}Incomplete
                      </Badge>
                    )}
                  </Col>
                </Row>
              </div>

              {/* Parent/Guardian Information */}
              <div className="mt-4 pt-4 border-top">
                <h6 className="mb-3 fw-bold text-dark">Parent/Guardian Information</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Print Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.parent_print_name}
                        onChange={(e) => handleInputChange('parent_print_name', e.target.value)}
                        placeholder="Print your full name"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                      <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                        <SignatureCanvas
                          ref={contractParentSignaturePadRef}
                          canvasProps={{
                            className: 'signature-canvas',
                            style: { width: '100%', height: '100%' }
                          }}
                        />
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => contractParentSignaturePadRef.current?.clear()}
                        type="button"
                      >
                        Clear Signature
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.parent_signature_date}
                        onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Registration Checklist Summary */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">checklist</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Registration Checklist Summary</h5>
              </div>
              <p className="text-muted mb-4">I confirm that I have submitted the following:</p>
              <div className="p-4 mb-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6 className="text-primary mb-3">
                        <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '20px' }}>description</i>
                        Required Forms
                      </h6>
                      <Form.Check
                        type="checkbox"
                        label="Registration Form"
                        checked={formData.checklist_registration_form}
                        onChange={(e) => handleInputChange('checklist_registration_form', e.target.checked)}
                        className="mb-3"
                        id="check-registration-form"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Emergency Form"
                        checked={formData.checklist_emergency_form}
                        onChange={(e) => handleInputChange('checklist_emergency_form', e.target.checked)}
                        className="mb-3"
                        id="check-emergency-form"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Child Information Page"
                        checked={formData.checklist_child_info_page}
                        onChange={(e) => handleInputChange('checklist_child_info_page', e.target.checked)}
                        className="mb-3"
                        id="check-child-info"
                      />
                      <Form.Check
                        type="checkbox"
                        label='"All About Me" Form'
                        checked={formData.checklist_all_about_me}
                        onChange={(e) => handleInputChange('checklist_all_about_me', e.target.checked)}
                        className="mb-3"
                        id="check-all-about-me"
                      />
                    </div>
                    <div className="mb-3">
                      <h6 className="text-primary mb-3">
                        <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '20px' }}>medical_services</i>
                        Health & Medical
                      </h6>
                      <Form.Check
                        type="checkbox"
                        label="Health Forms (Immunization)"
                        checked={formData.checklist_health_forms}
                        onChange={(e) => handleInputChange('checklist_health_forms', e.target.checked)}
                        className="mb-3"
                        id="check-health-forms"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Relevant medical/assessment reports (if any)"
                        checked={formData.checklist_medical_reports}
                        onChange={(e) => handleInputChange('checklist_medical_reports', e.target.checked)}
                        className="mb-3"
                        id="check-medical-reports"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6 className="text-primary mb-3">
                        <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '20px' }}>gavel</i>
                        Legal & Agreements
                      </h6>
                      <Form.Check
                        type="checkbox"
                        label="Birth Certificate"
                        checked={formData.checklist_birth_certificate}
                        onChange={(e) => handleInputChange('checklist_birth_certificate', e.target.checked)}
                        className="mb-3"
                        id="check-birth-cert"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Signed Contract"
                        checked={formData.checklist_signed_contract}
                        onChange={(e) => handleInputChange('checklist_signed_contract', e.target.checked)}
                        className="mb-3"
                        id="check-signed-contract"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Read and understood Parent Handbook"
                        checked={formData.checklist_parent_handbook}
                        onChange={(e) => handleInputChange('checklist_parent_handbook', e.target.checked)}
                        className="mb-3"
                        id="check-parent-handbook"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Waivers"
                        checked={formData.checklist_waivers}
                        onChange={(e) => handleInputChange('checklist_waivers', e.target.checked)}
                        className="mb-3"
                        id="check-waivers"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Off-Site Permission Form"
                        checked={formData.checklist_offsite_form}
                        onChange={(e) => handleInputChange('checklist_offsite_form', e.target.checked)}
                        className="mb-3"
                        id="check-offsite-form"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Meal Waiver (if applicable)"
                        checked={formData.checklist_meal_waiver}
                        onChange={(e) => handleInputChange('checklist_meal_waiver', e.target.checked)}
                        className="mb-3"
                        id="check-meal-waiver"
                      />
                    </div>
                    <div>
                      <h6 className="text-primary mb-3">
                        <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '20px' }}>payments</i>
                        Payment
                      </h6>
                      <Form.Check
                        type="checkbox"
                        label="Pre-Authorized Debit Form"
                        checked={formData.checklist_debit_form}
                        onChange={(e) => handleInputChange('checklist_debit_form', e.target.checked)}
                        className="mb-3"
                        id="check-debit-form"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              <Row className="mt-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={checklistSignaturePadRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => checklistSignaturePadRef.current?.clear()}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.parent_checklist_date}
                      onChange={(e) => handleInputChange('parent_checklist_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-4 p-4 text-center" style={{ backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #2196F3' }}>
                <h5 className="text-primary mb-0">Thank you for choosing Harmony Arts Daycare and Education Centre!</h5>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="d-flex justify-content-end gap-3 pt-4 mt-4 border-top">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={handleCancel}
                disabled={submitLoading}
                className="px-5"
              >
                <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>close</i>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={submitLoading}
                className="px-5"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
              >
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>check_circle</i>
                    Submit Registration
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Contract Clause Signature Modal */}
      <Modal show={showSignatureModal} onHide={handleCloseSignatureModal} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Modal.Title>
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>draw</i>
            Sign Clause {currentClauseIndex}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6 className="text-muted">Clause {currentClauseIndex}:</h6>
            <p className="fw-bold">
              {currentClauseIndex && [
                "I have reviewed and understood the Parent Policies Handbook (PPH).",
                "I agree to comply with all policies, including health, safety, and ministry regulations.",
                "I acknowledge that custody/visitation disputes require valid court documentation.",
                "I understand that while parental concerns are welcomed, certain policies are not subject to parental discretion.",
                "All required enrollment forms and documents will be submitted before the child's start date.",
                "I agree that tuition is due on the 1st of every month, and non-payment may result in suspended attendance.",
                "I grant permission for emergency medical care for my child if necessary.",
                "I agree to pay all extra-curricular activity fees in advance.",
                "I understand that 30 days' written notice is required for any withdrawal or schedule change.",
                "I understand that fees are non-refundable for absences, illness, or statutory holidays.",
                "I acknowledge that enrolment may be terminated for delinquent accounts, and I am responsible for any associated collection or legal fees.",
                "I am responsible for ensuring that all authorized persons follow pick-up procedures.",
                "I understand non-compliance with these terms may result in contract termination."
              ][currentClauseIndex - 1]}
            </p>
          </div>
          <div className="alert alert-info">
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
            Please sign in the box below to acknowledge this clause.
          </div>
          <div className="border rounded p-2 bg-light" style={{ width: '100%', height: '200px' }}>
            <SignatureCanvas
              ref={contractClauseSignatureRef}
              canvasProps={{
                className: 'signature-canvas',
                style: { width: '100%', height: '100%', backgroundColor: 'white' }
              }}
            />
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mt-2"
            onClick={() => contractClauseSignatureRef.current?.clear()}
          >
            <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>refresh</i>
            Clear Signature
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSignatureModal} disabled={signatureUploading}>
            <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>close</i>
            Cancel
          </Button>
          <Button
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
            onClick={handleSaveSignature}
            disabled={signatureUploading}
          >
            {signatureUploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>check_circle</i>
                Save Signature
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChildRegistrationFormComp;
